import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CompanyImage } from '@/Clients/CompanyClient';
import { useCompanyContext } from '@/Context/CompanyContext';
import { useNotificationContext } from '@/Context/NotificationContext';
import useCompanyId from '@/Hooks/UseCompanyId';
import SlugExistsError from '@/Utils/HTTP/Errors/SlugExistsError';
import UIStrings from '@/Utils/UIStrings';
import LoadingTab from '../../AccountSettingsTabs/LoadingTab';
import companyProfileSchema, { EditCompanyProfileForm } from './companyProfileValidationSchema';
import CompanyProfileForm from './CompanyProfileForm/CompanyProfileForm';
import CompanyUploadImageSection from './CompanyUploadImageSection';
import { CompanyProfile } from '@/Models/CompanyProfile';

const CompanyProfileTab = () => {
    const { currentCompanyProfile, updateCompanyProfile, refreshCurrentCompanyProfile } = useCompanyContext();
    const [savingInProgress, setSavingInProgress] = useState<boolean>(false);
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingFailed, setLoadingFailed] = useState<boolean>(false);
    const companyId = useCompanyId();
    const form = useForm({
        resolver: yupResolver(companyProfileSchema),
        defaultValues: async () => {
            try {
                const result = await refreshCurrentCompanyProfile(companyId);

                return result;
            } catch (error) {
                setLoadingFailed(true);
            } finally {
                setLoading(false);
            }

            return currentCompanyProfile;
        },
    });

    const submitHandler = async (values: EditCompanyProfileForm) => {
        try {
            setSavingInProgress(true);

            await updateCompanyProfile({
                ...currentCompanyProfile,
                ...values,
            } as CompanyProfile);
            showSuccessToaster(UIStrings.AccountDataSuccessfullyUpdated);
        } catch (error) {
            if (error instanceof SlugExistsError) {
                const { detail } = error;
                form.setError('companySlug', { type: 'custom', message: detail });
            }
            showFailToaster();
        } finally {
            setSavingInProgress(false);
        }
    };

    return (
        <LoadingTab isLoading={loading} loadingFailed={loadingFailed}>
            <FormProvider {...form}>
                <form encType="multipart/form-data" onSubmit={form.handleSubmit(submitHandler)}>
                    <CompanyProfileForm loading={savingInProgress}>
                        <CompanyUploadImageSection
                            circularCrop
                            title={UIStrings.Icon}
                            description={UIStrings.AScaledDownVersionOfYourLogo}
                            src={currentCompanyProfile.companyIconURL}
                            companyImageType={CompanyImage.Icon}
                        />
                        <CompanyUploadImageSection
                            title={UIStrings.CompanyLogo}
                            description={UIStrings.TheFullScaleVersionOfYourLogo}
                            src={currentCompanyProfile.companyLogoURL}
                            aspect={3}
                            companyImageType={CompanyImage.Logo}
                            className="h-18 w-52 rounded-xl"
                        />
                    </CompanyProfileForm>
                </form>
            </FormProvider>
        </LoadingTab>
    );
};

export default CompanyProfileTab;
